import "./OrdinalQuestionEditor.scss";

import { Flex } from "@fluentui/react-northstar";
import FormState, { FieldDescriptor, FieldDescriptors } from "@shopify/react-form-state";
import { SurveyLimits } from "@teams/cards";
import { randomString } from "@teams/client-shared";
import { OrdinalQuestion } from "@teams/types";
import React from "react";

import { Button } from "../../../../_shared/components/Button";
import { CheckboxField, TextInputField } from "../../../../_shared/components/fields";
import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { QuestionSettings } from "../../../../_shared/types";
import { AuthoringQuestionCollapseButton } from "./AuthoringQuestionCollapseButton";

export const OrdinalQuestionEditor: React.FC<{
  field: FieldDescriptors<OrdinalQuestion>;
  questionSettings: QuestionSettings;
  isLimitedEdit?: boolean;
  originalChoices?: string[];
  setCollapsed?: (collapsed: boolean) => void;
}> = ({ field, questionSettings, isLimitedEdit = true, setCollapsed, originalChoices = [] }) => {
  function addChoice() {
    if (field.choices.value.length < SurveyLimits.Questions.Choices.Max) {
      field.choices.onChange([
        ...field.choices.value,
        { id: randomString(), value: field.choices.value.length + 1, label: "" },
      ]);
    }
  }

  function removeChoice(id: string) {
    if (field.choices.value.length > SurveyLimits.Questions.Choices.Min) {
      field.choices.onChange([...field.choices.value.filter(f => f.id !== id)]);
    }
  }

  const isFieldDisabled =
    !!field.templateId || field.choices.value.length <= SurveyLimits.Questions.Choices.Min;

  return (
    <Flex column gap="gap.small">
      <Flex column gap="gap.small" className="ordinal-question__form-container">
        <FormState.List field={field.choices} getChildKey={c => c.id}>
          {(choice, index) => {
            const isOriginalChoice = originalChoices.includes(choice.id.value);
            return (
              <Flex gap="gap.small">
                <Flex.Item align="start" grow>
                  <div>
                    <TextInputField
                      placeholder={`Enter choice ${index + 1}`}
                      field={choice.label}
                      className="ordinal-question__choice-input"
                      fluid
                      clearable={!field.templateId}
                      readOnly={!!field.templateId}
                      maxLength={SurveyLimits.Questions.Choices.Label.MaxLength}
                      minLength={SurveyLimits.Questions.Choices.Label.MinLength}
                    />
                  </div>
                </Flex.Item>
                <Flex.Item className="ordinal-question__value-input" styles={{ minWidth: "35px" }}>
                  <div>
                    <TextInputField
                      field={choice.value}
                      className="ordinal-question__choice-input"
                      fluid
                      clearable={!field.templateId}
                      readOnly={!!field.templateId}
                    />
                  </div>
                </Flex.Item>
                {!field.templateId && (!isOriginalChoice || !isLimitedEdit) && (
                  <Flex.Item align="end">
                    <div>
                      <Button
                        className="trash-icon-button"
                        tooltip="Delete Choice"
                        disabled={isFieldDisabled}
                        iconOnly
                        onClick={e => {
                          removeChoice(choice.id.value);
                          e.preventDefault();
                        }}
                      >
                        <PollyIcon disabled={isFieldDisabled} icon="trash-icon" />
                      </Button>
                    </div>
                  </Flex.Item>
                )}
              </Flex>
            );
          }}
        </FormState.List>
        {!field.templateId && (
          <Button
            data-tname="ordinal-question-add-choice"
            fluid
            className="ordinal-question__add-button"
            disabled={field.choices.value.length >= SurveyLimits.Questions.Choices.Max}
            onClick={e => {
              addChoice();
              e.preventDefault();
            }}
            content={<PollyIcon icon="add-choice-icon" ignoreTheme label="Add choice" />}
          />
        )}
      </Flex>

      <Flex vAlign="end" gap="gap.medium" wrap>
        <Flex.Item grow>
          <>
            {!questionSettings.hideRequired && (
              <CheckboxField trueLabel="Required" falseLabel="Required" field={field.required} />
            )}

            {questionSettings.enableQuestionComments && (
              <CheckboxField
                field={field.allowComments as FieldDescriptor<boolean>}
                trueLabel="Comments"
                falseLabel="Comments"
                labelProps={{ weight: "regular" }}
              />
            )}
          </>
        </Flex.Item>
      </Flex>

      {questionSettings.enableQuestionComments && field.allowComments?.value && (
        <div>
          <TextInputField
            label="Comment message"
            maxLength={SurveyLimits.CommentAppeal.MaxLength}
            fluid
            field={field.commentAppeal as FieldDescriptor<string>}
            disabled={!field.allowComments?.value}
            labelProps={{ weight: "regular" }}
            placeholder="Please elaborate on your choice"
          />
        </div>
      )}
      <Flex fill hAlign="end">
        <AuthoringQuestionCollapseButton setCollapsed={setCollapsed} />
      </Flex>
    </Flex>
  );
};
